import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, X,
  MessageSquare, Type, Languages, Search, FileText, ListChecks, Pencil, HelpCircle,
  Sparkles, RefreshCw, Users,
  Glasses, Package, Cable, BookOpen, Feather, Puzzle,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { GlassPage } from '../components/chrome';
// Hosted Stripe payment link for the $50 reservation deposit.
const STRIPE_CHECKOUT_URL = 'https://buy.stripe.com/5kQ3cu6YI2Ed4BH2Uxe3e06';

const FEATURES = [
  { icon: MessageSquare, title: 'Caption', desc: 'Loud room, four people talking. Every word gets subtitled.' },
  { icon: Type, title: 'Recall', desc: 'Mid-sentence, you forget the word. It appears on the lens instantly.' },
  { icon: Languages, title: 'Translate', desc: 'The cashier speaks Italian, but you see English automatically.' },
  { icon: Search, title: 'Ask', desc: 'You or someone else asks a question. The answer is there instantly.' },
  { icon: FileText, title: 'Teleprompt', desc: 'Your script scrolls as you speak. You keep eye contact the whole presentation.' },
  { icon: ListChecks, title: 'Recap', desc: 'Forgot something after a conversation? The summary is waiting for you.' },
  { icon: Pencil, title: 'Note', desc: 'Say the idea. It is written down before you forget it.' },
  { icon: HelpCircle, title: 'Question', desc: 'Asked too many things at once? It is all displayed for you to answer.' },
];

const MARKETPLACE = [
  { icon: Sparkles, title: 'Add abilities', desc: 'Find what fits your day and add it in a tap. It lands on your lens with nothing to set up.' },
  { icon: RefreshCw, title: 'Always growing', desc: 'Updates come over the air, so the pair you buy this year keeps learning new tricks the next.' },
  { icon: Users, title: 'Built by many', desc: 'Anyone can build for the lens, so what KorsaOS can do never stops growing.' },
];

// Reserve-page product gallery. Files live in public/photos/. Swap the images
// there (keep the same filenames) to update what shows.
const RESERVE_PHOTOS = [
  { src: '/photos/reserve-front.png', alt: 'Korsa Glasses, front view on model' },
  { src: '/photos/reserve-angle.png', alt: 'Korsa Glasses, three-quarter view on model' },
  { src: '/photos/reserve-product.png', alt: 'Korsa Glasses product close-up' },
];

const BOX_ITEMS = [
  { icon: Glasses, label: 'Korsa Glasses' },
  { icon: Package, label: 'Charging case' },
  { icon: Cable, label: 'USB-C charging cable' },
  { icon: Feather, label: 'Microfiber cleaning cloth' },
  { icon: Puzzle, label: 'Replacement pieces' },
  { icon: BookOpen, label: 'Quick start guide' },
];

const FAQS = [
  { q: 'Where does my $50 go?', a: "It's held as a deposit against your pair. It counts toward your $500 total, and if you cancel before shipping you get all of it back." },
  { q: 'How do refunds work?', a: 'Email support@korsa.tech. We refund the full $50 to your original payment method.' },
  { q: 'What do I pay in total?', a: '$500 total: $50 now as a deposit, then $450 at shipping. The launch price will be $600 for anyone who waits past the early access deadline, so reserving now saves you $100 and holds your spot in case of a stock shortage.' },
  { q: 'When do the glasses ship?', a: "We're targeting Q4 2026. You'll be notified." },
  { q: 'What hardware are the glasses?', a: 'Korsa Glasses are manufactured for Korsa Inc., with KorsaOS, our operating system, integrated in.' },
  { q: 'Is this a finished product?', a: "This is an early reservation while we finish up our final touches." },
];

// --- FAQ popup ---

const FaqModal = ({ open, onClose }) => {
  const [openIdx, setOpenIdx] = useState(-1);
  const [render, setRender] = useState(false);
  const [show, setShow] = useState(false);

  // Mount immediately on open, then flip `show` next frame so the enter
  // transition runs. On close, play the exit transition, then unmount.
  useEffect(() => {
    if (open) {
      setRender(true);
      // Double rAF: let the browser paint the hidden initial state once before
      // flipping `show`, so the enter transition actually runs instead of
      // snapping straight to the final frame.
      let inner;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setShow(true));
      });
      return () => { cancelAnimationFrame(outer); cancelAnimationFrame(inner); };
    }
    setShow(false);
    const t = setTimeout(() => { setRender(false); setOpenIdx(-1); }, 300);
    return () => clearTimeout(t);
  }, [open]);

  // Lock page scroll + Escape-to-close while the overlay is in the DOM.
  useEffect(() => {
    if (!render) return;
    window.__lenis?.stop();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [render, onClose]);

  if (!render) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start md:items-center justify-center p-4 md:p-6" role="dialog" aria-modal="true" aria-label="Frequently asked questions">
      <div
        className={`absolute inset-0 bg-zinc-500/40 backdrop-blur-sm transition-opacity duration-300 ease-out ${show ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />
      <div
        className={`relative w-full max-w-xl max-h-[85vh] overflow-y-auto rounded-[2rem] bg-white shadow-2xl p-5 md:p-6 transition-all duration-300 ease-out ${show ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'}`}
      >
        <div className="flex justify-end mb-1">
          <button onClick={onClose} aria-label="Close" className="p-1 text-zinc-500 hover:text-zinc-900 transition-colors">
            <X size={22} />
          </button>
        </div>
        <div className="space-y-3 pb-2">
          {FAQS.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={item.q} className="rounded-2xl bg-zinc-100 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
                >
                  <span className="text-base md:text-lg font-bold text-zinc-900">{item.q}</span>
                  <Plus size={18} className={`shrink-0 text-zinc-500 transition-transform duration-300 ease-out ${isOpen ? 'rotate-45' : ''}`} />
                </button>
                <div className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                  <div className="overflow-hidden">
                    <p className="px-6 pb-5 text-sm md:text-base text-zinc-600 leading-relaxed">{item.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Reserve = () => {
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);

  // Tier selection: the launch tier can't be chosen. Clicking it slides the
  // white selection card down onto it, shakes "no", and slides back up.
  const [rejecting, setRejecting] = useState(false);
  const [showLaunchMsg, setShowLaunchMsg] = useState(false);

  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const selectorRef = useRef(null);
  const geomRef = useRef({ drop: 0, topH: 0, bottomH: 0 });
  const [sel, setSel] = useState({ top: 0, height: 0 });

  // Size the white selection card to the reserve tier, and record the launch
  // tier's offset and height so the reject animation covers it exactly.
  useLayoutEffect(() => {
    const measure = () => {
      if (!topRef.current || !bottomRef.current) return;
      const t = topRef.current;
      const b = bottomRef.current;
      geomRef.current = {
        drop: b.offsetTop - t.offsetTop,
        topH: t.offsetHeight,
        bottomH: b.offsetHeight,
      };
      setSel({ top: t.offsetTop, height: t.offsetHeight });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(topRef.current);
    ro.observe(bottomRef.current);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  const rejectLaunch = () => {
    if (rejecting) return;
    setRejecting(true);
    setShowLaunchMsg(true);

    // Slide the white card down so it covers the launch tier exactly (it
    // morphs to that tier's height), shake "no", then slide back up.
    const el = selectorRef.current;
    const { drop, topH, bottomH } = geomRef.current;
    if (el && el.animate) {
      const down = `translateY(${drop}px)`;
      el.animate(
        [
          { transform: 'translateY(0) translateX(0)', height: `${topH}px` },
          { transform: `${down} translateX(0)`, height: `${bottomH}px`, offset: 0.2 },
          { transform: `${down} translateX(-5px)`, height: `${bottomH}px`, offset: 0.32 },
          { transform: `${down} translateX(5px)`, height: `${bottomH}px`, offset: 0.44 },
          { transform: `${down} translateX(-5px)`, height: `${bottomH}px`, offset: 0.56 },
          { transform: `${down} translateX(5px)`, height: `${bottomH}px`, offset: 0.68 },
          { transform: `${down} translateX(0)`, height: `${bottomH}px`, offset: 0.78 },
          { transform: 'translateY(0) translateX(0)', height: `${topH}px` },
        ],
        { duration: 1300, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
      );
    }

    setTimeout(() => setRejecting(false), 1350);
    setTimeout(() => setShowLaunchMsg(false), 4500);
  };

  // Smooth-scroll down to the KorsaOS section on this page. Lenis owns the
  // scroll position, so drive it through the shared instance (same pattern as
  // the home page's section links); fall back to native smooth scroll.
  const scrollToProduct = () => {
    const el = document.getElementById('korsaos');
    if (!el) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(el, { offset: -80 });
    } else {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Send buyers straight to the hosted Stripe payment link for the deposit.
  const startCheckout = () => {
    if (redirecting) return;
    setError(false);
    setRedirecting(true);
    window.location.assign(STRIPE_CHECKOUT_URL);
  };

  return (
    <GlassPage>
      <FaqModal open={faqOpen} onClose={() => setFaqOpen(false)} />

      {/* Full-screen product hero */}
      <div className="max-w-[1600px] mx-auto px-4 md:px-6 pt-24 md:pt-28">
        <section className="glass overflow-hidden">
          <div className="grid lg:grid-cols-[1.7fr_1fr] lg:items-stretch">
            {/* Left: product gallery (square, fills edge to edge) */}
            <div className="group relative aspect-square bg-white border-b lg:border-b-0 lg:border-r border-white/70 overflow-hidden">
              {RESERVE_PHOTOS.map((p, i) => (
                <img
                  key={p.src}
                  src={p.src}
                  alt={p.alt}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out ${i === photoIdx ? 'opacity-100' : 'opacity-0'}`}
                />
              ))}

              {/* Hover navigation arrows */}
              <button
                type="button"
                onClick={() => setPhotoIdx((i) => (i - 1 + RESERVE_PHOTOS.length) % RESERVE_PHOTOS.length)}
                aria-label="Previous photo"
                className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 backdrop-blur-md border border-white shadow-[0_4px_16px_rgba(31,38,135,0.15)] flex items-center justify-center text-zinc-800 opacity-0 group-hover:opacity-100 hover:bg-white transition-all duration-200"
              >
                <ChevronLeft size={20} strokeWidth={2.2} />
              </button>
              <button
                type="button"
                onClick={() => setPhotoIdx((i) => (i + 1) % RESERVE_PHOTOS.length)}
                aria-label="Next photo"
                className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 backdrop-blur-md border border-white shadow-[0_4px_16px_rgba(31,38,135,0.15)] flex items-center justify-center text-zinc-800 opacity-0 group-hover:opacity-100 hover:bg-white transition-all duration-200"
              >
                <ChevronRight size={20} strokeWidth={2.2} />
              </button>

              {/* Thumbnail selector */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2.5">
                {RESERVE_PHOTOS.map((p, i) => (
                  <button
                    key={p.src}
                    type="button"
                    onClick={() => setPhotoIdx(i)}
                    aria-label={`Show ${p.alt}`}
                    aria-pressed={i === photoIdx}
                    className={`h-14 w-14 rounded-2xl overflow-hidden border transition-all duration-300 ${i === photoIdx ? 'border-zinc-900 ring-2 ring-zinc-900/10 scale-105' : 'border-white/80 opacity-70 hover:opacity-100'}`}
                  >
                    <img src={p.src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {/* Right: info + pricing */}
            <div className="flex flex-col justify-center px-6 md:px-10 xl:px-14 py-8 lg:py-6">
              <div className="text-center mb-4">
                <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase text-zinc-900">Korsa Glasses</h1>
              </div>

              {/* Tabs */}
              <div className="flex items-center justify-center gap-3 mb-5">
                <button
                  type="button"
                  onClick={scrollToProduct}
                  className="rounded-full border border-zinc-300 px-6 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-900 hover:bg-zinc-900 hover:text-white hover:border-zinc-900 transition-colors"
                >
                  Product
                </button>
                <button
                  type="button"
                  onClick={() => setFaqOpen(true)}
                  className="rounded-full border border-zinc-300 px-6 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-900 hover:bg-zinc-900 hover:text-white hover:border-zinc-900 transition-colors"
                >
                  FAQ
                </button>
              </div>

              {/* NEO-style stacked pricing: gray shell, white card marks the
                  selected tier and physically rejects the launch tier */}
              <div className="relative rounded-[1.5rem] bg-zinc-200/60 mt-1.5 p-2">
                <div
                  ref={selectorRef}
                  className="pointer-events-none absolute left-2 right-2 z-0 rounded-[1.2rem] bg-white shadow-[0_10px_30px_rgba(31,38,135,0.14)]"
                  style={{ top: sel.top, height: sel.height }}
                />

                {/* Early Access tier (selected) */}
                <div ref={topRef} className="relative z-10 p-3.5 md:p-4 mb-1.5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-bold text-zinc-900 mb-1.5">Early Access</p>
                      <ul className="space-y-1 text-[13px] text-zinc-600 list-disc pl-5">
                        <li>Fully refundable $50 deposit</li>
                        <li>$450 due only at ship</li>
                      </ul>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-base font-semibold text-zinc-400 line-through leading-none">$500</p>
                      <p className="text-lg font-bold tracking-tight text-zinc-900 mt-0.5">$50</p>
                      <p className="text-xs text-zinc-500 font-medium">Today</p>
                    </div>
                  </div>
                </div>

                {/* Launch tier (not selectable) */}
                <button
                  type="button"
                  ref={bottomRef}
                  onClick={rejectLaunch}
                  aria-disabled="true"
                  className="relative z-10 w-full text-left p-3.5 md:p-4 cursor-not-allowed"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-bold text-zinc-900 mb-1.5">Launch Day</p>
                      <ul className="space-y-1 text-[13px] text-zinc-600 list-disc pl-5">
                        <li>Full retail price</li>
                        <li>Standard delivery</li>
                      </ul>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold tracking-tight text-zinc-900">$600</p>
                      <p className="text-xs text-zinc-400 font-medium">Launch price</p>
                    </div>
                  </div>
                </button>
              </div>

              <p className={`text-center text-[11px] uppercase tracking-widest font-bold text-zinc-900 mt-3 transition-opacity duration-300 ${showLaunchMsg ? 'opacity-100' : 'opacity-0'}`}>
                That is the launch price. Reserve now to lock $500.
              </p>

              {/* Deposit summary */}
              <div className="text-center mt-4">
                <p className="text-lg font-bold text-zinc-900">$50 Deposit Due Today</p>
                <p className="text-sm font-bold text-zinc-900">Fully refundable</p>
                <p className="text-sm text-zinc-500">US deliveries start 2026</p>
              </div>

              {/* Order */}
              <div className="mt-5 flex flex-col items-center">
                <button
                  type="button"
                  onClick={startCheckout}
                  disabled={redirecting}
                  className={`btn-dark px-12 py-4 ${redirecting ? 'opacity-60 cursor-wait' : ''}`}
                >
                  {redirecting ? 'Opening checkout...' : 'Order Now'}
                </button>
                {error && (
                  <p className="mt-3 text-[10px] uppercase tracking-widest text-red-500 font-bold">
                    Checkout didn't open. Please try again.
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* In the box + Prescription */}
      <section className="py-16 md:py-24 px-4 md:px-6">
        <div className="glass max-w-[1600px] mx-auto px-8 md:px-16 py-16 md:py-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">

            {/* In the box */}
            <div>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tighter uppercase text-zinc-900 mb-8">What's included</h2>
              <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
                {BOX_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="flex items-center gap-3 rounded-2xl bg-white/80 border border-white shadow-[0_10px_30px_rgba(31,38,135,0.10)] px-5 py-4">
                      <Icon size={20} strokeWidth={2} className="text-zinc-600 shrink-0" />
                      <span className="text-sm md:text-base font-bold text-zinc-900">{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Prescription */}
            <div className="lg:border-l lg:border-zinc-900/5 lg:pl-16">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tighter uppercase text-zinc-900 mb-6">Prescription ready</h2>
              <p className="text-base text-zinc-600 leading-relaxed mb-8">
                Prescription lenses are available for free. When the glasses are ready to
                ship we will email you with a checkout where you will be able to add
                prescription information and pay the rest of the $450.
              </p>
              <p className="text-sm text-zinc-500">
                Questions about your prescription? Email <a href="mailto:support@korsa.tech" className="text-zinc-900 underline underline-offset-2">support@korsa.tech</a>.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* KorsaOS + Marketplace */}
      <section id="korsaos" className="py-16 md:py-24 px-4 md:px-6 scroll-mt-24">
        <div className="glass max-w-[1600px] mx-auto px-8 md:px-16 py-16 md:py-20">
          <div className="mb-10 md:mb-14 text-center">
            <h2 className="text-4xl md:text-6xl font-bold tracking-tighter text-zinc-900">Korsa<span className="text-zinc-500">OS</span></h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="rounded-2xl bg-white/80 border border-white shadow-[0_10px_30px_rgba(31,38,135,0.12)] p-5 hover:-translate-y-1 transition-transform duration-300 flex flex-col items-center text-center">
                  <Icon size={20} strokeWidth={2} className="text-zinc-600 mb-3" />
                  <h3 className="text-lg font-bold tracking-tight text-zinc-900 mb-1.5">{f.title}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Marketplace */}
          <div className="mt-12 md:mt-16 pt-10 md:pt-12 border-t border-zinc-900/5">
            <div className="text-center mb-8 md:mb-10">
              <h3 className="text-3xl md:text-5xl font-bold tracking-tighter uppercase text-zinc-900 mb-4">The marketplace</h3>
              <p className="text-base text-zinc-600 leading-relaxed max-w-xl mx-auto">
                One pair of glasses that keeps getting better. New abilities arrive the way
                apps do on a phone.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 md:gap-5">
              {MARKETPLACE.map((m) => {
                const Icon = m.icon;
                return (
                  <div key={m.title} className="rounded-2xl bg-white/80 border border-white shadow-[0_10px_30px_rgba(31,38,135,0.12)] p-5 md:p-6 hover:-translate-y-1 transition-transform duration-300 flex flex-col items-center text-center">
                    <Icon size={20} strokeWidth={2} className="text-zinc-600 mb-3" />
                    <h3 className="text-lg font-bold tracking-tight text-zinc-900 mb-1.5">{m.title}</h3>
                    <p className="text-sm text-zinc-600 leading-relaxed">{m.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </GlassPage>
  );
};

export const ReserveThanks = () => (
  <GlassPage>
    <div className="max-w-lg mx-auto px-4 md:px-6 pt-28 md:pt-36">
      <section className="glass px-8 md:px-12 py-12 md:py-16 text-center">
        <div className="mx-auto w-16 h-16 rounded-full border border-green-500/30 bg-green-500/10 flex items-center justify-center text-green-600 mb-8">
          <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase text-zinc-900 mb-4">Spot reserved</h1>
        <p className="text-sm md:text-base text-zinc-600 leading-relaxed mb-4">
          Your $50 is held, not spent. It locks your $500 price and stays fully refundable
          until your glasses ship: email <a href="mailto:support@korsa.tech" className="text-zinc-900 underline underline-offset-2">support@korsa.tech</a> anytime
          and we'll return it, no reason needed.
        </p>
        <p className="text-sm md:text-base text-zinc-600 leading-relaxed mb-10">
          We're targeting 2026 for shipping. That's an estimate, and we'll email you
          updates as the date firms up. The remaining $450 is due when your glasses ship.
        </p>
        <Link to="/" className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900 transition-colors">
          Back to home →
        </Link>
      </section>
    </div>
  </GlassPage>
);

export default Reserve;
